/**
 * Common Utilities for CuriousJr Web Revamp
 * Shared utility functions across the application
 */

// import * as Constants from "@/constants/common"
import { BATCH_TYPE } from '@/lib/enums/batch';
import { ServerAnalyticsPlatform, ServerAnalyticsSource, UserType } from '@/types/auth';
import { BOOSTER_BANNER_VARIANT, CJR_BOOSTER_BANNER_STATE } from '@/types/common';
import { MLP_TYPE } from '@/types/marketing';
import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { UAParser } from 'ua-parser-js';

// Local Storage utilities
export const getLocalStorageValue = (key: string, defaultValue?: any) => {
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      const localStored = localStorage.getItem(key);
      if (typeof defaultValue === 'string' || defaultValue === undefined || defaultValue === null) {
        return localStored || defaultValue;
      }
      // JSON.parse only works for valid objects
      const val = (localStored && JSON.parse(localStored)) || defaultValue;
      return val;
    }
    return defaultValue;
  } catch (error) {
    console.error(`Error getting localStorage value for key "${key}":`, error);
    return localStorage.getItem(key) || defaultValue;
  }
};

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export const setLocalStorageValue = (key: string, value: string): void => {
  if (typeof window === 'undefined') return;

  try {
    localStorage.setItem(key, value);
  } catch (error) {
    console.warn(`Failed to set localStorage item '${key}':`, error);
  }
};

export const removeLocalStorageValue = (key: string): void => {
  if (typeof window === 'undefined') return;

  try {
    localStorage.removeItem(key);
  } catch (error) {
    console.warn(`Failed to remove localStorage item '${key}':`, error);
  }
};

// Validation utilities
export const isPhoneValid = (phone: string, isIndianPhone: boolean = true) => {
  const _text = phone || '';
  let pattern = /^[5-9]\d{5,13}$/;
  if (!isIndianPhone) {
    pattern = /^[1-9]\d{5,13}$/;
  }
  return pattern.test(_text);
};

export const isStudentNameValid = (name: string, isMessageReturned = false) => {
  let message = '';

  if (!name) {
    message = 'Please enter your name!';
  } else if (name.length < 3) {
    message = 'Name should be atleast 3 characters long!';
  } else if (name.toLowerCase() === 'null') {
    message = "Name can't be " + name;
  } else if (!isNameValid(name)) {
    message = 'Please enter a valid name!';
  }

  // return isMessageReturned ? message : true
  if (isMessageReturned) {
    return message;
  } else return !!message;
};

export const isNameValid = (address: string) => {
  const _text = address || '';
  // let pattern = /^[a-zA-Z ]+$/g
  const pattern =
    /([0-9]|([$&+,:;=?@#|'<>.^*()%!-])|wallah|walah|Physic|helpline|customer|support|number|group|toll|free|help|whatsapp|telegram|career|institute|emergency|airtel|apollo|academy|verification|telephone|employee|coordinator|admin|team|Alakh Pandey|Noida Headquarter|Pathshala|Vidyapeeth|UPSC|GATE|CLAT|SSC|BANKING|CSIR|IITJAM|batch manager|coach|Faculty|teacher|professor|exam|test|library|dpp|khazana|quiz|Store|Offline|Online|Scholarship|Offer|VSME|SME|PW SME|PW VSME|AlakhPandey|NoidaHeadquarter|batchmanager|PWSME|PWVSME|IITJAM|IIT JAM)/gi;

  return (
    pattern.test(_text) === false &&
    address?.length < 32 &&
    address &&
    address.replace(/ /g, '')?.length
  );
};

export const isEmailValid = (email: string) => {
  const _text = email || '';
  const pattern =
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return pattern.test(_text);
};

export const isOTPValid = (otp: string): boolean => {
  const otpRegex = /^\d{6}$/;
  return otpRegex.test(otp);
};

// Script loading utility
export const loadScriptById = (id: string, src: string): Promise<boolean> => {
  return new Promise(resolve => {
    if (typeof window === 'undefined') {
      resolve(false);
      return;
    }

    // Check if script already exists
    if (document.getElementById(id)) {
      resolve(true);
      return;
    }

    const script = document.createElement('script');
    script.id = id;
    script.src = src;
    script.async = true;

    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);

    document.head.appendChild(script);
  });
};

export const findBatchType = (batchDetails: any): string => {
  return (batchDetails as any)?.config?.cjrBatchType === BATCH_TYPE.BOOSTER
    ? BATCH_TYPE.BOOSTER
    : BATCH_TYPE.MASTER;
};

// Session Storage utilities
export const getSessionStorageValue = (key: string): string | null => {
  if (typeof window === 'undefined') return null;

  try {
    return sessionStorage.getItem(key);
  } catch (error) {
    console.warn(`Failed to get sessionStorage item '${key}':`, error);
    return null;
  }
};

export const setSessionStorageValue = (key: string, value: string): void => {
  if (typeof window === 'undefined') return;

  try {
    sessionStorage.setItem(key, value);
  } catch (error) {
    console.warn(`Failed to set sessionStorage item '${key}':`, error);
  }
};

// URL utilities
export const getQueryParam = (name: string): string | null => {
  if (typeof window === 'undefined') return null;

  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get(name);
};

export const setQueryParam = (name: string, value: string): void => {
  if (typeof window === 'undefined') return;

  const urlParams = new URLSearchParams(window.location.search);
  urlParams.set(name, value);

  const newUrl = `${window.location.pathname}?${urlParams.toString()}`;
  window.history.replaceState({}, '', newUrl);
};

// String utilities
export const capitalize = (str: string): string => {
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

export const slugify = (str: string): string => {
  return str
    .toLowerCase()
    .replace(/[^\w\s-]/g, '') // Remove special characters
    .replace(/[\s_-]+/g, '-') // Replace spaces and underscores with hyphens
    .replace(/^-+|-+$/g, ''); // Remove leading/trailing hyphens
};

// Array utilities
export const unique = <T>(array: T[]): T[] => {
  return [...new Set(array)];
};

export const groupBy = <T, K extends keyof T>(array: T[], key: K): Record<string, T[]> => {
  return array.reduce(
    (groups, item) => {
      const groupKey = String(item[key]);
      if (!groups[groupKey]) {
        groups[groupKey] = [];
      }
      groups[groupKey].push(item);
      return groups;
    },
    {} as Record<string, T[]>
  );
};

// Date utilities
export const formatDate = (
  date: Date | string,
  format: 'short' | 'long' | 'iso' = 'short'
): string => {
  const dateObj = typeof date === 'string' ? new Date(date) : date;

  switch (format) {
    case 'short':
      return dateObj.toLocaleDateString();
    case 'long':
      return dateObj.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    case 'iso':
      return dateObj.toISOString();
    default:
      return dateObj.toLocaleDateString();
  }
};

export const isDateExpired = (date: Date | string): boolean => {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  return dateObj.getTime() < Date.now();
};

// Device detection
export const isMobile = (): boolean => {
  if (typeof window === 'undefined') return false;

  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
};

export const isTablet = (): boolean => {
  if (typeof window === 'undefined') return false;

  return (
    /iPad|Android|Silk|Kindle/i.test(navigator.userAgent) && !/Mobile/i.test(navigator.userAgent)
  );
};

export const isDesktop = (): boolean => {
  return !isMobile() && !isTablet();
};

// Environment detection
export const isDevelopment = (): boolean => {
  return process.env.NODE_ENV === 'development';
};

export const isProduction = (): boolean => {
  return process.env.NODE_ENV === 'production';
};

// Debounce utility
export const debounce = <T extends (...args: any[]) => any>(
  func: T,
  wait: number
): ((...args: Parameters<T>) => void) => {
  let timeout: NodeJS.Timeout | null = null;

  return (...args: Parameters<T>) => {
    if (timeout) {
      clearTimeout(timeout);
    }

    timeout = setTimeout(() => func(...args), wait);
  };
};

// Throttle utility
export const throttle = <T extends (...args: any[]) => any>(
  func: T,
  limit: number
): ((...args: Parameters<T>) => void) => {
  let inThrottle: boolean;

  return (...args: Parameters<T>) => {
    if (!inThrottle) {
      func(...args);
      inThrottle = true;
      setTimeout(() => (inThrottle = false), limit);
    }
  };
};

// Deep clone utility
export const deepClone = <T>(obj: T): T => {
  if (obj === null || typeof obj !== 'object') {
    return obj;
  }

  if (obj instanceof Date) {
    return new Date(obj.getTime()) as unknown as T;
  }

  if (obj instanceof Array) {
    return obj.map(item => deepClone(item)) as unknown as T;
  }

  if (typeof obj === 'object') {
    const clonedObj = {} as T;
    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        clonedObj[key] = deepClone(obj[key]);
      }
    }
    return clonedObj;
  }

  return obj;
};

// Random utilities
export const generateId = (length: number = 8): string => {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let result = '';

  for (let i = 0; i < length; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }

  return result;
};

export const randomBetween = (min: number, max: number): number => {
  return Math.floor(Math.random() * (max - min + 1)) + min;
};

/**
 * Checks if a value is empty.
 * Returns true if the value is empty, otherwise false.
 */
export const isEmpty = (value: any) => {
  // Check for null, undefined, or empty string
  if (value == null || value === undefined || value === '') return true;

  // Check for empty array
  if (Array.isArray(value) && value.length === 0) return true;

  // Check for empty object
  if (typeof value === 'object' && !Array.isArray(value)) {
    return Object.keys(value).length === 0;
  }

  // Non-empty
  return false;
};

export const getUserTypeFromPaidBatch = (paid_batch_type?: string): UserType => {
  if (!paid_batch_type || paid_batch_type.trim() === '') {
    return UserType.Free;
  }

  const type = paid_batch_type.toLowerCase();

  switch (type) {
    case 'booster':
      return UserType.Demo;
    case 'both':
    case 'master':
      return UserType.Master;
    default:
      return UserType.Free;
  }
};

export const getErrorObject = (value: any) => {
  return {
    ...value,
    name: value?.name,
    message: value?.message,
    stack: value?.stack,
  };
};

export function getTokenFromLocalStorage(): string | null {
  if (typeof window !== 'undefined') {
    const token = localStorage.getItem('TOKEN')
    return token || null
  }
  return null
}

export const getUserName = (user: any): string => {
  return user?.lastName ? `${user?.firstName}${user?.lastName}` : user?.firstName || '';
};

export const getMlpUtm = (mlpType: any): MLP_TYPE => {
  if (!mlpType) {
    return '' as MLP_TYPE;
  }
  return mlpType as MLP_TYPE;
};

export const getServerAnalyticsPlatform = (): keyof typeof ServerAnalyticsPlatform => {
  const deviceDetails = new UAParser().getResult();
  if (deviceDetails.os.name === 'iOS') return ServerAnalyticsPlatform.IOS;
  else if (deviceDetails.os.name === 'Android') return ServerAnalyticsPlatform.ANDROID;
  else return ServerAnalyticsPlatform.WEB;
};

export const getServerSource = (): keyof typeof ServerAnalyticsSource | undefined => {
  if (typeof window !== 'undefined' && window.localStorage.getItem('from_app') === 'true')
    return ServerAnalyticsSource.APP;
  else if (window.location.pathname === '/') return ServerAnalyticsSource.FORM;
  else if (window.location.pathname === '/login') return ServerAnalyticsSource.LOGIN;
  // for now we are sending the same source for all types of marketing pages
  else if (window.location.pathname.includes('/marketing')) return ServerAnalyticsSource.MLP;
};

export const getCurrencyIcon = (currency: string) => {
  switch (currency) {
    case 'INR':
      return '₹';
    case 'USD':
      return '$';
    case 'AED':
      return 'AED';
    case 'QAR':
      return 'QAR';
    case 'SAR':
      return 'SAR';
    case 'BHD':
      return 'BHD';
    case 'KWD':
      return 'KWD';
    case 'OMR':
      return 'OMR';
    default:
      return currency;
  }
};

export const getUserData = () => {
  const user = getLocalStorageValue('user', {});
  return user;
};

export function clearLocalStorage() {
  if (typeof window !== 'undefined') {
    // Preserve COUNTRY_LOCATION as it should persist across user sessions
    const countryLocation = localStorage.getItem('COUNTRY_LOCATION');

    localStorage.removeItem('CLIENT_ID');
    localStorage.removeItem('username');
    localStorage.removeItem('countryCode');
    localStorage.removeItem('token');
    localStorage.removeItem('REFRESH_TOKEN');
    localStorage.removeItem('expires_in');
    localStorage.removeItem('user');
    localStorage.removeItem('TOKEN');
    localStorage.removeItem('documentUrl');
    localStorage.removeItem('shouldRegister');
    localStorage.removeItem('last_selected_batch');
    localStorage.removeItem('last_selected_cohort_pre_login');
    localStorage.removeItem('last_selected_cohort_post_login');
    localStorage.removeItem('last_selected_batch_subject');

    // Restore COUNTRY_LOCATION if it existed
    if (countryLocation) {
      localStorage.setItem('COUNTRY_LOCATION', countryLocation);
      console.log('🔄 Preserved COUNTRY_LOCATION during logout:', countryLocation);
    }
  }
}

export const downloadFile = async (url: string, fileName: string) => {
  await fetch(url).then(response => {
    response.blob().then(blob => {
      // Creating new object of PDF file
      const fileURL = window.URL.createObjectURL(blob);
      // Setting various property values
      const alink = document.createElement('a');
      alink.href = fileURL;
      alink.download = `${fileName}.pdf`;
      alink.click();
    });
  });
};

export const getOrdinalSuffix = (day: number) => {
  if (day > 3 && day < 21) return `${day}th`;
  switch (day % 10) {
    case 1:
      return `${day}st`;
    case 2:
      return `${day}nd`;
    case 3:
      return `${day}rd`;
    default:
      return `${day}th`;
  }
};

// to start with 0 if single digit is present
export const padTime = (time: number | string, maxLength = 2) => {
  return time.toString().padStart(maxLength, '0');
};

export const getBoosterBannerEventVariant = (variant: BOOSTER_BANNER_VARIANT) => {
  switch (variant) {
    case CJR_BOOSTER_BANNER_STATE.DAYWISE:
      return 'old';
    case CJR_BOOSTER_BANNER_STATE.IMAGE:
      return 'static';
    case CJR_BOOSTER_BANNER_STATE.VIDEO:
      return 'gif';
    default:
      return '';
  }
};

/**
 *
 * @param classes array of strings representing class numbers e.g. ['1', '2', '3']
 * @returns removed array format in string format e.g. ['1', '2', '3'] -> 1,2,3 or ['4', '0', '3'] -> 4,0,3
 */
export const getEventClasses = (classes: string[]): string => {
  return JSON.stringify((classes || []).map(Number)).slice(1, -1);
};

export const openWhatsappUrl = (number: string, text: string) => {
  const a = document.createElement('a');
  try {
    a.href = `https://api.whatsapp.com/send?text=${encodeURI(text)}&phone=${number}&abid=${number}`;
    a.click();
  } catch (error) {
    console.log('WhatsaApp not present');
  }
};

/**
 *
 * @returns color of the prev section, this is used to set the top color of wave section
 */

export const getPrevSectionBgColorHome = ({
  isPostPurchaseVisible,
  isMentorBannerVisible,
  isMasterSectionVisible,
  isHeroSectionVisible,
  isBrainBytesVisible,
  isChallengeOfTheDayVisible,
  isLearnADayVisible,
  isCohortSwitcherVisible,
  isReferralVisible,
  isSelectedBatchBooster,
}: {
  isPostPurchaseVisible: boolean;
  isMentorBannerVisible: boolean;
  isMasterSectionVisible: boolean;
  isHeroSectionVisible: boolean;
  isBrainBytesVisible: boolean;
  isChallengeOfTheDayVisible: boolean;
  isLearnADayVisible: boolean;
  isCohortSwitcherVisible: boolean;
  isReferralVisible: boolean;
  isSelectedBatchBooster: boolean;
}) => {
  const defaultBg: Record<string, string> = {
    postPurchase: isSelectedBatchBooster ? '#f3fbff' : '#FFFDF8',
    master: '#1B2124',
    hero: '#fff',
    brainBytes: '#035582',
    challengeOfTheDay: '#1e1e2b',
    learnADay: '#fff',
    cohortSwitcher: '#1e1e2b',
    referral: '#fff',
  };
  defaultBg.mentorBanner = window.innerWidth < 640 ? '#EFBF04' : defaultBg.postPurchase;

  const res: Partial<Record<keyof typeof defaultBg, string>> = {};
  const getAfterPostPurchaseBgColor = () => {
    if (isPostPurchaseVisible && isMasterSectionVisible) {
      return defaultBg.master;
    } else if (isPostPurchaseVisible && isMentorBannerVisible) {
      return defaultBg.mentorBanner;
    } else if (isPostPurchaseVisible) {
      return defaultBg.postPurchase;
    }
  };

  if (isBrainBytesVisible) {
    if (isHeroSectionVisible) {
      res.brainBytes = defaultBg.hero;
    } else {
      res.brainBytes = getAfterPostPurchaseBgColor();
    }
  }

  if (isChallengeOfTheDayVisible) {
    if (isBrainBytesVisible) {
      res.challengeOfTheDay = defaultBg.brainBytes;
    } else if (isHeroSectionVisible) {
      res.challengeOfTheDay = defaultBg.hero;
    } else {
      res.challengeOfTheDay = getAfterPostPurchaseBgColor();
    }
  }
  if (isCohortSwitcherVisible || isReferralVisible) {
    let color = '';
    if (isLearnADayVisible) {
      color = defaultBg.learnADay;
    } else if (isChallengeOfTheDayVisible) {
      color = defaultBg.challengeOfTheDay;
    } else if (isBrainBytesVisible) {
      color = defaultBg.brainBytes;
    } else if (isHeroSectionVisible) {
      color = defaultBg.hero;
    } else {
      color = getAfterPostPurchaseBgColor() || '';
    }
    res.cohortSwitcher = color;
    res.referral = color;
  }
  return res;
};
